import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, brl } from "@/lib/api";
import { Badge, Button } from "@/components/ui-kit";
import { ShoppingBag, RotateCcw, Store, ScanBarcode } from "lucide-react";

const dt = (iso) => new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });

/**
 * Histórico de compras do cliente, com recompra.
 *
 * Balcão e loja online aparecem juntos porque, para quem atende, é uma coisa só:
 * "o que essa pessoa já levou daqui". É esta lista que responde a pergunta do
 * balcão — "qual era mesmo a bengala que a senhora comprou?" — e é dela que sai
 * o botão de refazer.
 */
export default function PurchaseHistory({ customer, onClose }) {
  const [state, setState] = useState({ loading: true, data: [], summary: null });
  const [repeating, setRepeating] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let vivo = true;
    api.get(`/admin/customers/${customer.id}/purchases`)
      .then((r) => vivo && setState({ loading: false, data: r.data.data || [], summary: r.data.summary }))
      .catch(() => vivo && setState({ loading: false, data: [], summary: null }));
    return () => { vivo = false; };
  }, [customer.id]);

  /**
   * Refazer: o carrinho é remontado com os preços de HOJE e o PDV abre com ele.
   *
   * A revalidação é do servidor de propósito — copiar o preço da compra antiga
   * faria o vendedor cobrar um valor que não existe mais e prometer peça que já
   * foi vendida. O PDV recebe os itens já conferidos e mostra o que mudou.
   */
  const refazer = async (compra) => {
    if (repeating) return;
    setRepeating(compra.id);
    try {
      const { data } = await api.get(`/admin/customers/${customer.id}/purchases/${compra.id}/repeat`);
      if (!data.items?.length) {
        toast.error("Os produtos desta compra não estão mais no catálogo.");
        return;
      }
      onClose?.();
      // Estado de rota, não sessionStorage: o carrinho é de uso imediato e não
      // deve sobreviver a um F5 nem vazar para outra aba do balcão.
      navigate("/admin/pdv", { state: { repeat: data } });
    } catch (e) { toast.error(apiError(e)); }
    finally { setRepeating(null); }
  };

  if (state.loading) return <p className="text-sm text-slate-400 py-8 text-center">Carregando compras…</p>;

  if (!state.data.length) {
    return (
      <div className="py-10 text-center">
        <ShoppingBag size={28} className="mx-auto text-slate-300 mb-3" aria-hidden />
        <p className="text-sm font-medium text-slate-600">Nenhuma compra registrada</p>
        <p className="text-xs text-slate-400 mt-1">Vendas do balcão e pedidos da loja aparecem aqui.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="crm-purchases">
      {state.summary && (
        <div className="flex flex-wrap gap-4 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-sm">
          <span className="text-slate-600">Compras: <b className="text-slate-900">{state.summary.count}</b></span>
          <span className="text-slate-600">Total: <b className="font-mono text-slate-900">{brl(state.summary.total)}</b></span>
          {state.summary.last_at && <span className="text-slate-600">Última: <b className="text-slate-900">{dt(state.summary.last_at)}</b></span>}
        </div>
      )}

      <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
        {state.data.map((c) => (
          <li key={c.id} className="px-3 py-3" data-testid="crm-purchase-row">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 flex-wrap">
                  <span className={`font-mono text-sm font-semibold ${c.cancelled ? "text-slate-400 line-through" : "text-slate-900"}`}>{c.number}</span>
                  {/* O canal muda o que o vendedor pode prometer (a loja tem
                      frete, o balcão não), então fica visível na linha. */}
                  <Badge tone={c.origin === "balcao" ? "default" : "info"}>
                    {c.origin === "balcao"
                      ? <><ScanBarcode size={11} className="inline mr-1" aria-hidden />Balcão</>
                      : <><Store size={11} className="inline mr-1" aria-hidden />Loja</>}
                  </Badge>
                  {c.cancelled && <Badge tone="danger" dot>{c.status_label}</Badge>}
                  <span className="text-xs text-slate-400">{dt(c.at)}</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {c.items.map((i) => `${i.qty}× ${i.name}${i.variant_label ? ` (${i.variant_label})` : ""}`).join(" · ") || "—"}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className={`font-mono text-sm font-semibold ${c.cancelled ? "text-slate-400 line-through" : "text-slate-900"}`}>{brl(c.total)}</p>
                {/* Compra cancelada não vira sugestão de recompra pelo caminho
                    fácil: ela está no histórico como registro, não como oferta. */}
                {!c.cancelled && (
                  <Button variant="link" size="sm" icon={RotateCcw} className="mt-0.5"
                    loading={repeating === c.id} onClick={() => refazer(c)} data-testid="crm-purchase-repeat">
                    Refazer
                  </Button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
